import { Component, computed, input, output, signal } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EPlatform } from '@enums/campaign.enum';
import { TranslateModule } from '@ngx-translate/core';
import { IChannel } from '@pages/creators/creators.interface';
import { IconComponent } from '@shared/components/icon/icon.component';
import { SocialBadgeComponent } from '@shared/components/social-badge/social-badge.component';
import { camelCase } from 'lodash';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

@Component({
  imports: [
    SocialBadgeComponent,
    NgxSkeletonLoaderModule,
    MatTooltipModule,
    TranslateModule,
    MatMenuModule,
    IconComponent,
  ],
  selector: 'app-creator-channels-filter',
  styleUrls: ['./creator-channels-filter.component.scss'],
  templateUrl: './creator-channels-filter.component.html',
})
export class CreatorChannelsFilterComponent {
  public readonly channels = input.required<IChannel[] | null>();

  public readonly loading = input<boolean>(false);

  public readonly channelsChanged = output<Set<string>>();

  public readonly selectedChannels = signal<Set<IChannel>>(new Set());

  public readonly channelOptions = computed(() => {
    return new Set(
      this.channels()?.map((channel) => ({ ...channel, platform: camelCase(channel.platform) as EPlatform })) || [],
    );
  });

  public readonly moreChannelsSelected = computed(() => {
    return this.channelsOrdered()
      .slice(4)
      .some((channel) => this.selectedChannels().has(channel));
  });

  public readonly channelsOrdered = computed(() =>
    Array.from(this.channelOptions() || []).sort((a, b) => a.platformCreatorId.localeCompare(b.platformCreatorId)),
  );

  public onChannelClick(channel: IChannel | null, event?: Event): void {
    event?.stopImmediatePropagation();

    if (!channel && this.selectedChannels().size === 0) {
      return;
    }

    if (!channel) {
      this.selectedChannels.set(new Set());
      this.channelsChanged.emit(new Set());

      return;
    }

    this.selectedChannels.update((prev) => {
      const newSet = new Set(prev);

      if (newSet.has(channel)) {
        newSet.delete(channel);
      } else {
        newSet.add(channel);
      }

      if (this.channelOptions().size === newSet.size && this.channelOptions().size !== 1) {
        newSet.clear();
      }

      this.channelsChanged.emit(new Set(Array.from(newSet).map((c) => `${c.platformTechnicalId}_${c.platform}`)));

      return newSet;
    });
  }

  public getChannelTracking(channel: IChannel): string {
    return `${channel.platformTechnicalId}-${channel.platformCreatorId}-${channel.platform}`;
  }
}
